import { getEnv } from "../config/env";
import { API_TIMEOUT_MS } from "../config/constants";

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  timeoutMs?: number;
  /**
   * Internal flag set by apiFetch's own 401-retry path to prevent infinite
   * refresh loops. Callers should never set this — it's exposed only to keep
   * the recursive call type-safe.
   */
  __retried?: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    super(`API ${status} ${statusText}`);
    this.name = "ApiError";
  }
}

/**
 * Auth bridge — wired by the app layer (AuthGate) at startup so the shared
 * fetch-client never has to import from the entities layer (FSD discipline).
 * `getToken` is called on every outgoing request; `refresh` is called once
 * on a 401 response. `refresh` returns true if a new token is now available
 * (in which case the original request is retried once) and false otherwise
 * (in which case the 401 propagates as ApiError).
 */
export interface AuthBridge {
  getToken: () => string | null;
  refresh: () => Promise<boolean>;
}

let authBridge: AuthBridge | null = null;
let authToken: string | null = null;

/** Production wiring — called from app/providers/AuthGate. */
export function setAuthBridge(bridge: AuthBridge | null): void {
  authBridge = bridge;
}

/**
 * Test/dev shim. When no AuthBridge is registered, apiFetch falls back to
 * this module-level token. Production code paths use setAuthBridge.
 */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

function currentToken(): string | null {
  if (authBridge) return authBridge.getToken();
  return authToken;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, timeoutMs = API_TIMEOUT_MS, headers, __retried, ...rest } = options;
  const env = getEnv();
  const url = path.startsWith("http")
    ? path
    : `${env.VITE_API_URL.replace(/\/$/, "")}${path}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const token = currentToken();
  const init: RequestInit = {
    ...rest,
    signal: controller.signal,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(headers as Record<string, string> | undefined),
    },
  };
  if (body !== undefined) {
    init.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(url, init);
    const contentType = response.headers.get("content-type") ?? "";
    const parsed = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
    if (!response.ok) {
      // Single 401-retry path: only attempt refresh when a bridge is wired,
      // we haven't already retried this request, and the failure is a 401.
      // Any other status (incl. 403, which means "authenticated but not
      // permitted") propagates immediately.
      if (response.status === 401 && authBridge && !__retried) {
        const refreshed = await authBridge.refresh();
        if (refreshed) {
          clearTimeout(timeout);
          return apiFetch<T>(path, { ...options, __retried: true });
        }
      }
      throw new ApiError(response.status, response.statusText, parsed);
    }
    return parsed as T;
  } finally {
    clearTimeout(timeout);
  }
}
